/**
 * useBoardBoxSelect - Box selection for board tasks
 * Click+drag on empty space draws a selection rectangle and selects intersecting tasks.
 * Uses selectionStore.isBoxSelecting to signal active drag to other components.
 * Dispatches directly to selectionStore instead of using callbacks.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { setDragBodyStyles, stopDragBodyStyles } from '../../lib/dragResizeUtils';
import { selectionStore } from '../stores/selectionStore';

interface TaskRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface UseBoxSelectOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  taskPositions: Map<string, TaskRect>;
  isDndDragging: boolean;
  isResizing: boolean;
}

export interface UseBoxSelectResult {
  isSelecting: boolean;
  selectionRect: { x: number; y: number; width: number; height: number } | null;
}

type Phase = 'idle' | 'pending' | 'selecting';

function rectsIntersect(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function normalizeRect(x1: number, y1: number, x2: number, y2: number) {
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}

export function useBoardBoxSelect({
  containerRef,
  contentRef,
  taskPositions,
  isDndDragging,
  isResizing,
}: UseBoxSelectOptions): UseBoxSelectResult {
  const [selectionRect, setSelectionRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const phaseRef = useRef<Phase>('idle');
  const startBoardRef = useRef({ x: 0, y: 0 });
  const additiveRef = useRef(false);
  const isDndDraggingRef = useRef(isDndDragging);
  const isResizingRef = useRef(isResizing);
  const taskPositionsRef = useRef(taskPositions);

  isDndDraggingRef.current = isDndDragging;
  isResizingRef.current = isResizing;
  taskPositionsRef.current = taskPositions;

  const getBoardCoords = useCallback((e: PointerEvent) => {
    const content = contentRef.current;
    if (!content) return { x: 0, y: 0 };
    const contentRect = content.getBoundingClientRect();
    return {
      x: e.clientX - contentRect.left,
      y: e.clientY - contentRect.top,
    };
  }, [contentRef]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    function handlePointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      if (e.defaultPrevented) return;
      if ((e.target as HTMLElement).closest('[data-task-id]')) return;
      if (isDndDraggingRef.current || isResizingRef.current) return;

      const coords = getBoardCoords(e);
      startBoardRef.current = coords;
      additiveRef.current = e.shiftKey;
      phaseRef.current = 'pending';
    }

    function handlePointerMove(e: PointerEvent) {
      if (phaseRef.current === 'idle') return;

      const current = getBoardCoords(e);
      const dx = current.x - startBoardRef.current.x;
      const dy = current.y - startBoardRef.current.y;

      if (phaseRef.current === 'pending') {
        if (Math.abs(dx) + Math.abs(dy) < 5) return;
        phaseRef.current = 'selecting';
        setIsSelecting(true);
        setDragBodyStyles('crosshair');
        selectionStore.send({ type: 'boxSelect.start' });
      }

      if (phaseRef.current === 'selecting') {
        const rect = normalizeRect(
          startBoardRef.current.x,
          startBoardRef.current.y,
          current.x,
          current.y,
        );
        setSelectionRect(rect);

        // Find intersecting tasks
        const matching: string[] = [];
        for (const [taskId, pos] of taskPositionsRef.current) {
          if (rectsIntersect(rect, pos)) {
            matching.push(taskId);
          }
        }
        if (additiveRef.current) {
          selectionStore.send({ type: 'selection.add', taskIds: matching });
        } else {
          selectionStore.send({ type: 'selection.set', taskIds: matching });
        }
      }
    }

    function handlePointerUp() {
      if (phaseRef.current === 'pending') {
        // Click on empty space without drag = clear selection
        selectionStore.send({ type: 'selection.clear' });
      }
      if (phaseRef.current === 'selecting') {
        stopDragBodyStyles();
        // Delay boxSelect.end so the subsequent click event
        // still sees isBoxSelecting=true in the store
        requestAnimationFrame(() => {
          selectionStore.send({ type: 'boxSelect.end' });
        });
      }
      phaseRef.current = 'idle';
      setIsSelecting(false);
      setSelectionRect(null);
    }

    content.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      content.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [contentRef, getBoardCoords]);

  return { isSelecting, selectionRect };
}
